/**
 * The `use` interaction (R1.1) — applying one thing to another, gated by
 * conditions, producing effects and narrative.
 *
 * @remarks
 * Moved out of `items.ts` at the 500-line cap when D234 gave an item `weapon`
 * and `armor`. Nothing changed but the address: `items.ts` re-exports both
 * names, so every import of them is unmoved. D235 added the GIVE interaction
 * beside it — the same shape, read when a thing is handed to a body.
 *
 * @packageDocumentation
 */

import { z } from 'zod';

import { conditionSchema } from './conditions.js';
import { effectSchema } from './effects.js';
import { exitIdSchema, itemIdSchema } from './identifiers.js';
import { narrativeLineSchema } from './transcript.js';

/**
 * One authored `use` interaction: applying THIS item to a target, gated by
 * conditions, producing effects and narrative. At most one of
 * `targetExitId` / `targetItemId` may be named (enforced by refinement —
 * "use keycard on gate" targets an exit; "use cutter on manifold" targets an
 * item). `narratives` is a LIST (R1.1): one interaction routinely speaks on
 * several channels at once (reader beeps in `system`, door moves in
 * `environment`, EURYDICE comments in `eurydice`).
 *
 * D245 (the Meridian play-test's A1): an arm that names NO target is the
 * thing used on its own — `use the autodoc`, `use the defibrillator`. It
 * answers the bare sentence when its conditions hold (first such arm wins,
 * as everywhere); when none holds, the engine asks what to use it on, as it
 * always has. The same arm, the same fields, one target fewer: the two-object
 * arms are this one with somewhere to point.
 */
export const useInteractionSchema = z
	.strictObject({
		conditions: z.array(conditionSchema).readonly().default([]),
		// `effects` may be empty (R4/D19): `narratives` is min-1, so every
		// interaction has a consequence — a NARRATIVE-ONLY interaction is real
		// authored fiction ("use pry bar on security door" earns a refusal in
		// EURYDICE's voice without changing any state).
		effects: z.array(effectSchema).readonly().default([]),
		narratives: z.array(narrativeLineSchema).min(1).readonly(),
		targetExitId: exitIdSchema.optional(),
		targetItemId: itemIdSchema.optional(),
	})
	.readonly()
	.refine((interaction) => interaction.targetExitId === undefined || interaction.targetItemId === undefined, {
		message: 'A use interaction names at most one target: targetExitId or targetItemId (neither is the thing used on its own)',
		path: ['targetItemId'],
	});

/** One authored use-interaction — see {@link useInteractionSchema}. */
export type UseInteraction = z.infer<typeof useInteractionSchema>;

/**
 * D245: is this the thing used on its own — an arm that names no target?
 *
 * @param interaction - One use interaction
 * @returns Whether it answers a bare `use <thing>`
 */
export const isUsedAlone = (interaction: UseInteraction): boolean =>
	interaction.targetExitId === undefined && interaction.targetItemId === undefined;

/**
 * D235 (actors round B2; `docs/design/actors.md` §4): one authored GIVE
 * interaction on an actor — what happens when the player hands THIS item to
 * the body, gated by conditions, producing effects and narrative. The `use`
 * interaction's shape verbatim, with the target implied (the actor the table
 * sits on) and one field of its own: `consumesItem`, which removes the item
 * from the hands when the actor keeps it. `show <item> to <actor>` reads
 * the same table with `consumesItem` forced false, so one authored answer
 * serves both verbs — a thing shown is still in your hands afterward.
 */
export const giveInteractionSchema = z
	.strictObject({
		/**
		 * D244 (Act II §13.7): which verb this arm answers — `give` (handed
		 * over) or `show` (held up). Absent, both, as every arm before D244
		 * did. So a trade happens on `give` and only an appraisal on `show`:
		 * Bede prices what you hold up and pays for what you hand him. A
		 * `show` arm that keeps the thing is refused at load (a thing shown is
		 * never kept).
		 */
		act: z.enum(['give', 'show']).optional(),
		conditions: z.array(conditionSchema).readonly().default([]),
		/** Whether the actor KEEPS the thing — removed from the hands after the narratives and before the effects. Default false: it is offered and handed back, or merely shown. */
		consumesItem: z.boolean().default(false),
		effects: z.array(effectSchema).readonly().default([]),
		/** The item handed over — something the player can carry, or the arm could never run (the loader says so). */
		itemId: itemIdSchema,
		narratives: z.array(narrativeLineSchema).min(1).readonly(),
	})
	.readonly();

/** One authored give-interaction — see {@link giveInteractionSchema}. */
export type GiveInteraction = z.infer<typeof giveInteractionSchema>;
